import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
  Chip,
  Divider
} from '@mui/material';
import { 
  Slideshow,
  Download,
  Share,
  MoreVert,
  ArrowBack,
  Delete,
  FileDownload
} from '@mui/icons-material';
import { API_URL, fetchWithCredentials } from '../config';

const PresentPage = () => {
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load presentations from backend (Firestore) on mount
  useEffect(() => {
    loadPresentations();
  }, []);

  const loadPresentations = async () => {
    setLoading(true);
    try {
      const response = await fetchWithCredentials(`${API_URL}/api/presentations/`);
      if (response.ok) {
        const data = await response.json();
        setPresentations(data.presentations || []);
        console.log(`✅ Loaded ${data.presentations?.length || 0} presentations from Firestore`);
      } else {
        console.error('Failed to load presentations:', response.status);
        setPresentations([]);
      }
    } catch (error) {
      console.error('Error loading presentations:', error);
      setPresentations([]);
    } finally {
      setLoading(false);
    }
  };

  const [viewMode, setViewMode] = useState('list');
  const [showTitleSection] = useState(true);
  const [selectedPresentation, setSelectedPresentation] = useState(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [activeMenuId, setActiveMenuId] = useState(null);

  const handlePreview = (presentation) => {
    setSelectedPresentation(presentation);
    setCurrentSlideIndex(0);
    setViewMode('preview');
    handleMenuClose();
  };

  const handleMenuOpen = (e, id) => {
    setAnchorEl(e.currentTarget);
    setActiveMenuId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveMenuId(null);
  };

  const handleExportPowerPoint = async (presentation) => {
    try {
      console.log('Exporting to PowerPoint:', presentation.id);
      
      const response = await fetchWithCredentials(`${API_URL}/api/presentations/export-pptx`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          slides: presentation.slides || [],
          title: presentation.title,
          audience: presentation.audience || 'general',
          description: presentation.description
        })
      });

      if (!response.ok) {
        throw new Error('Failed to export presentation');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${presentation.title}.pptx`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error exporting presentation:', err);
      alert('Failed to export presentation: ' + err.message);
    } finally {
      handleMenuClose();
    }
  };

  const handleExportPDF = (presentation) => {
    console.log('Exporting to PDF:', presentation.id);
    alert('PDF export coming soon');
    handleMenuClose();
  };

  const handleShare = (presentation) => {
    setSelectedPresentation(presentation);
    setShareDialogOpen(true);
    handleMenuClose();
  };

  const handleShareSubmit = () => {
    console.log(`Sharing presentation "${selectedPresentation.title}" with ${shareEmail}`);
    setShareEmail('');
    setShareDialogOpen(false);
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetchWithCredentials(`${API_URL}/api/presentations/${id}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        console.log(`✅ Deleted presentation ${id}`);
        // Reload presentations from backend
        loadPresentations();
      } else {
        console.error('Failed to delete presentation:', response.status);
      }
    } catch (error) {
      console.error('Error deleting presentation:', error);
    }
    handleMenuClose();
  };

  const getAudienceLabel = (audience) => {
    const labels = {
      executives: 'Executives',
      marketing: 'Marketing Team',
      merchandising: 'Merchandising',
      finance: 'Finance',
      operations: 'Operations',
      board: 'Board',
      general: 'General'
    };
    return labels[audience] || audience;
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Title Section */}
      {showTitleSection && (
        <Box sx={{ px: 4, py: 3, backgroundColor: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Slideshow sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
              Presentations
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Generate and export polished slide decks from your analysis sessions
          </Typography>
        </Box>
      )}

      {/* Header */}
      <Box sx={{ 
        px: 2,
        py: 1.5,
        borderBottom: 1, 
        borderColor: 'divider',
        backgroundColor: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            size="small" 
            onClick={() => setViewMode('list')}
            sx={{ color: 'white' }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="subtitle1" sx={{ 
            fontWeight: 600,
            color: 'white',
            fontSize: '0.95rem'
          }}>
            Your Presentations
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          {presentations.length} presentation{presentations.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {/* Preview Mode */}
      {viewMode === 'preview' && selectedPresentation && (
        <Box sx={{ flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', p: 2 }}>
          {/* Slide Viewer */}
          <Box sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 3,
            backgroundColor: '#f5f5f5',
            minHeight: 400,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            mb: 2,
            flex: 1
          }}>
            {selectedPresentation.slides && selectedPresentation.slides[currentSlideIndex] && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                  Slide {currentSlideIndex + 1} of {selectedPresentation.slides.length}
                </Typography>

                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  {selectedPresentation.slides[currentSlideIndex].title}
                </Typography>

                {selectedPresentation.slides[currentSlideIndex].subtitle && (
                  <Typography variant="subtitle1" sx={{ mb: 2, color: 'text.secondary' }}>
                    {selectedPresentation.slides[currentSlideIndex].subtitle}
                  </Typography>
                )}

                {selectedPresentation.slides[currentSlideIndex].content && (
                  <Box sx={{ mb: 2 }}>
                    {typeof selectedPresentation.slides[currentSlideIndex].content === 'string' ? (
                      <Typography variant="body2">{selectedPresentation.slides[currentSlideIndex].content}</Typography>
                    ) : (
                      <ul style={{ margin: '0 0 0 20px', paddingLeft: 0 }}>
                        {selectedPresentation.slides[currentSlideIndex].content.map((item, idx) => (
                          <li key={idx} style={{ marginBottom: '8px', fontSize: '0.95rem' }}>
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Navigation */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
            <Button
              onClick={() => setCurrentSlideIndex(Math.max(0, currentSlideIndex - 1))}
              disabled={currentSlideIndex === 0}
            >
              Previous
            </Button>

            <Typography variant="body2" sx={{ alignSelf: 'center' }}>
              {currentSlideIndex + 1} / {selectedPresentation.slides?.length || 0}
            </Typography>

            <Button
              onClick={() => setCurrentSlideIndex(Math.min((selectedPresentation.slides?.length || 1) - 1, currentSlideIndex + 1))}
              disabled={currentSlideIndex === (selectedPresentation.slides?.length || 1) - 1}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}

      {/* Presentations List */}
      {viewMode === 'list' && (
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {presentations.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {presentations.map((presentation) => (
              <Paper key={presentation.id} elevation={1} sx={{ p: 2, '&:hover': { elevation: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                        {presentation.title}
                      </Typography>
                      <Chip 
                        label={getAudienceLabel(presentation.audience)} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {presentation.description}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        📅 {new Date(presentation.createdDate).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        📊 {presentation.slideCount} slides
                      </Typography>
                    </Box>
                  </Box>

                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, presentation.id)}
                  >
                    <MoreVert />
                  </IconButton>
                </Box>

                {/* Quick Action Buttons */}
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FileDownload />}
                    onClick={() => handleExportPowerPoint(presentation)}
                  >
                    PowerPoint
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={() => handleExportPDF(presentation)}
                  >
                    PDF
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Share />}
                    disabled
                  >
                    Coming Soon
                  </Button>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Slideshow sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              No Presentations Yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create a presentation from a chat session in the Plan tab
            </Typography>
          </Box>
        )}
      </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handlePreview(presentations.find(p => p.id === activeMenuId))}>
          <Slideshow sx={{ mr: 1, fontSize: 18 }} />
          Preview
        </MenuItem>
        <MenuItem onClick={() => handleExportPowerPoint(presentations.find(p => p.id === activeMenuId))}>
          <FileDownload sx={{ mr: 1, fontSize: 18 }} />
          Export to PowerPoint
        </MenuItem>
        <MenuItem onClick={() => handleExportPDF(presentations.find(p => p.id === activeMenuId))}>
          <Download sx={{ mr: 1, fontSize: 18 }} />
          Export to PDF
        </MenuItem>
        <MenuItem onClick={() => handleShare(presentations.find(p => p.id === activeMenuId))}>
          <Share sx={{ mr: 1, fontSize: 18 }} />
          Share
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem 
          onClick={() => handleDelete(activeMenuId)}
          sx={{ color: 'error.main' }}
        >
          <Delete sx={{ mr: 1, fontSize: 18 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Share Presentation</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Share "{selectedPresentation?.title}" with team members
          </Typography>
          <TextField
            fullWidth
            label="Email address"
            type="email"
            value={shareEmail}
            onChange={(e) => setShareEmail(e.target.value)}
            placeholder="colleague@company.com"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleShareSubmit} 
            variant="contained"
            disabled={!shareEmail.trim()}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PresentPage;
